import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CK_CONFIGS } from "configs/ckEditor";
import { Row, Col, Form } from "react-bootstrap";
import validate from "validate.js";
import { forEach, isEmpty, find } from "lodash";
import {
  addIncidentResolution,
  getIncidentById,
  hideIncidentValidationError,
  updateIncidentResolution,
  redirectToIncidentList,
} from "actions";

const schema = {
  subject: {
    presence: { allowEmpty: false, message: "^Subject is required" },
  },
};

const ResolutionTab = ({
  incidentId,
  setIncidentId,
  previousData,
  onNext,
  onBack,
  loadFromBack,
  setLoadFromBack,
}) => {
  const dispatch = useDispatch();
  const incidentState = useSelector((state) => state.incidentState);
  const [resolutions, setResolutions] = useState([
    { subject: "", resolution_description: "" },
  ]);

  const [isFinalReportGenerated, setIsFinalReportGenerated] = useState(false);

  const [formState, setFormState] = useState({
    isValid: false,
    errors: [{}],
    touched: [{}],
  });

  useEffect(() => {
    if (!isEmpty(incidentState.validation_error)) {
      const errors = incidentState.validation_error;
      setFormState((formState) => ({
        ...formState,
        isValid: errors ? false : true,
        errors: errors || {},
      }));
    }
  }, [incidentState.validation_error]);

  useEffect(() => {
    if (loadFromBack) {
      dispatch(getIncidentById(incidentId, "update"));
      setLoadFromBack(false);
    }
  }, [loadFromBack]);

  useEffect(() => {
    if (!previousData) return;

    if (previousData.status === "final_report") {
      setIsFinalReportGenerated(true);
    }

    const hasResolutions =
      Array.isArray(previousData.resolutions) &&
      previousData.resolutions.length > 0;

    const mappedResolutions = hasResolutions
      ? previousData.resolutions.map((res) => ({
          subject: res.subject || "",
          resolution_description: res.resolution_description || "",
        }))
      : [
          {
            subject: "",
            resolution_description: "",
          },
        ];

    setResolutions(mappedResolutions);

    setFormState((prev) => ({
      ...prev,
      touched: mappedResolutions.map(() => ({})),
      errors: mappedResolutions.map(() => ({})),
    }));
  }, [previousData]);

  // Validate each resolution individually
  useEffect(() => {
    const newErrors = resolutions.map((item) => validate(item, schema) || {});
    const isValid = newErrors.every((e) => Object.keys(e).length === 0);
    setFormState((prev) => ({
      ...prev,
      errors: newErrors,
      isValid,
    }));
  }, [resolutions]);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...resolutions];
    updated[index][name] = value;
    setResolutions(updated);

    const touched = [...formState.touched];
    touched[index] = { ...touched[index], [name]: true };
    setFormState((prev) => ({ ...prev, touched }));
  };

  const setDescription = (index, data) => {
    const updated = [...resolutions];
    updated[index].resolution_description = data;
    setResolutions(updated);

    const touched = [...formState.touched];
    touched[index] = { ...touched[index], resolution_description: true };
    setFormState((prev) => ({ ...prev, touched }));

    dispatch(hideIncidentValidationError("resolution_description"));
  };

  const hasError = (index, field) =>
    formState.touched[index]?.[field] && formState.errors[index]?.[field];

  const handleAddMore = () => {
    setResolutions([
      ...resolutions,
      { subject: "", resolution_description: "" },
    ]);
    setFormState((prev) => ({
      ...prev,
      errors: [...prev.errors, {}],
      touched: [...prev.touched, {}],
    }));
  };

  const handleDelete = (indexToDelete) => {
    const updatedResolutions = resolutions.filter(
      (_, i) => i !== indexToDelete
    );
    const updatedErrors = formState.errors.filter(
      (_, i) => i !== indexToDelete
    );
    const updatedTouched = formState.touched.filter(
      (_, i) => i !== indexToDelete
    );

    setResolutions(updatedResolutions);
    setFormState((prev) => ({
      ...prev,
      errors: updatedErrors,
      touched: updatedTouched,
    }));
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();

    const formattedResolutions = resolutions.map((item, index) => ({
      ...item,
      resolution_order: index + 1, // ✅ assign order based on index
    }));

    const payload = {
      incident_id: incidentId,
      resolutions: formattedResolutions,
    };

    try {
      const response = await dispatch(addIncidentResolution(payload));
      if (response?.data?.id) {
        setIncidentId(response.data.id);
        await dispatch(getIncidentById(response.data.id, "update"));
        onNext();
      }
    } catch (error) {
      console.error("Failed to save resolution:", error);
    }
  };

  const handleSave = async (event) => {
    if (event) event.preventDefault();

    const formattedResolutions = resolutions.map((item, index) => ({
      ...item,
      resolution_order: index + 1,
    }));

    const payload = {
      incident_id: incidentId,
      resolutions: formattedResolutions,
    };

    try {
      const response = await dispatch(addIncidentResolution(payload));
      if (response?.data?.id) {
        setIncidentId(response.data.id);
        await dispatch(getIncidentById(response.data.id, "update"));
      }
    } catch (error) {
      console.error("Failed to save resolution:", error);
    }
  };

  return (
    <>
      <div className="main-content-div">
        {resolutions.map((item, index) => (
          <>
            {isFinalReportGenerated ? (
              <>
                {!isEmpty(item.subject) ? (
                  <div key={item.id} className="border rounded mb-3">
                    <div className="action-content d-flex align-items-center justify-content-between">
                      <h2 className="font-13 font-weight-s-bold heading-color mb-0 d-flex align-items-center">
                        Ressolution# {index + 1}: {item.subject || "N/A"}
                      </h2>

                      <div className="d-flex"></div>
                    </div>
                    <div className="p-4">
                      <Row>
                        <Col lg={12}>
                          <div>
                            <h2 className="font-13 font-weight-normal heading-color">
                              Ressolution Description:
                            </h2>
                            <p
                              className="font-13 font-weight-normal text-color"
                              dangerouslySetInnerHTML={{
                                __html: item.resolution_description || "N/A",
                              }}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </>
            ) : (
              <div
                key={index}
                className="mb-4 border rounded px-2 pb-3 position-relative"
              >
                <Row>
                  <Col lg={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Subject</Form.Label>
                      <Form.Control
                        type="text"
                        name="subject"
                        placeholder="Enter Subject"
                        className="input-field"
                        value={item.subject}
                        onChange={(e) => handleChange(index, e)}
                        isInvalid={hasError(index, "subject")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {hasError(index, "subject") &&
                          formState.errors[index].subject[0]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">
                        Description
                      </Form.Label>
                      <CKEditor
                        editor={ClassicEditor}
                        config={CK_CONFIGS(localStorage.getItem("token"))}
                        data={item.resolution_description}
                        onChange={(event, editor) =>
                          setDescription(index, editor.getData())
                        }
                      />
                    </Form.Group>
                  </Col>
                  {/* Delete button */}
                  {resolutions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleDelete(index)}
                      className="bg-transparent border-0 text-end"
                      title="Delete"
                    >
                      <img src="/assets/images/delete-icon.svg" alt="Delete" />
                    </button>
                  )}
                </Row>
              </div>
            )}
          </>
        ))}

        {!isFinalReportGenerated ? (
          <>
            <div className="mb-3">
              <button
                type="button"
                className="add-more-btn"
                onClick={handleAddMore}
              >
                <img src="/assets/images/add-more-icon.svg" alt="" /> Add more
              </button>
            </div>

            <div className="d-flex justify-content-start gap-3">
              <button className="c-b-btn" onClick={onBack}>
                <img className="me-2" src="/assets/images/back-arrow.svg" />{" "}
                Back
              </button>
              <button
                className="n-u-btn"
                onClick={handleSubmit}
                disabled={!formState.isValid}
              >
                Next
                <img
                  className="ms-2"
                  src="/assets/images/next-icon.svg"
                  alt=""
                />
              </button>
              <button className="save-btn" onClick={handleSave}>
                Save
              </button>
            </div>
          </>
        ) : (
          ""
        )}
      </div>
      <div className="mt-3">
        <button
          className="c-b-btn"
          onClick={() => {
            dispatch(redirectToIncidentList());
          }}
        >
          Back To List
        </button>
      </div>
    </>
  );
};

export default ResolutionTab;
